import type { Camera } from '@anov/3d-core'
import { EventDispatcher, Vector3 } from '@anov/3d-core'
import { gsap } from 'gsap'

class FollowOrbitControls extends EventDispatcher {
  radius: number
  target: Vector3

  private object: Camera
  private domElement: HTMLElement

  private rotationH: number
  private rotationV: number
  private isMouseDown: boolean
  private isCurrentFirstMove: boolean
  private isGlobalFirstMove: boolean

  constructor(object: Camera, domElement: HTMLElement) {
    super()

    this.object = object
    this.domElement = domElement
    this.target = new Vector3()
    this.radius = this.getDistance()
    this.rotationH = Math.atan2(this.object.position.x, this.object.position.z)
    this.rotationV = Math.acos(this.object.position.y / this.radius)

    this.isMouseDown = false
    this.isCurrentFirstMove = true
    this.isGlobalFirstMove = true

    this.domElement.addEventListener('mousedown', this.handleMousedown.bind(this))
    this.domElement.addEventListener('mouseup', this.handleMouseup.bind(this))
    this.domElement.addEventListener('mousemove', this.handleMousemove.bind(this))
    this.domElement.addEventListener('wheel', this.handleWheelEvent.bind(this))
  }

  dispose() {
    this.domElement.removeEventListener('mousedown', this.handleMousedown.bind(this))
    this.domElement.removeEventListener('mouseup', this.handleMouseup.bind(this))
    this.domElement.removeEventListener('mousemove', this.handleMousemove.bind(this))
    this.domElement.removeEventListener('wheel', this.handleWheelEvent.bind(this))
  }

  update() {
    this.object.lookAt(this.target)
  }

  private getDistance() {
    return this.object.position.distanceTo(this.target)
  }

  private handleMouseup() {
    this.isMouseDown = false
  }

  private handleMousedown() {
    this.isMouseDown = true
    this.isCurrentFirstMove = true
  }

  /**
   * handle Mousemove
   * @param event
   * @returns
   */
  private handleMousemove(event: MouseEvent) {
    if (!this.isMouseDown)
      return

    if (this.isGlobalFirstMove) {
      this.isGlobalFirstMove = false
      this.dispatchEvent({
        type: 'start',
      })
    }

    if (this.isCurrentFirstMove) {
      this.isCurrentFirstMove = false
      this.rotationH = Math.atan2(this.object.position.x, this.object.position.z)
      this.rotationV = Math.acos(this.object.position.y / this.radius)

      return
    }

    this.rotationH -= (event.movementX / 180) * Math.PI
    this.rotationV -= (event.movementY / 180) * Math.PI

    if (this.rotationV < Math.PI / 180)
      this.rotationV = Math.PI / 180
    if (this.rotationV > Math.PI - Math.PI / 180)
      this.rotationV = Math.PI - Math.PI / 180

    this.updateCameraPosition()
  }

  private handleWheelEvent(event: WheelEvent) {
    event.preventDefault()

    if (this.isGlobalFirstMove) {
      this.isGlobalFirstMove = false
      this.dispatchEvent({
        type: 'start',
      })
    }

    if (this.isCurrentFirstMove) {
      this.isCurrentFirstMove = false
      this.rotationH = Math.atan2(this.object.position.x, this.object.position.z)
      this.rotationV = Math.acos(this.object.position.y / this.radius)

      return
    }

    const zoomSpeed = 0.3
    const minRadius = 0.1
    const maxRadius = 100000
    const targetRadius = this.radius + event.deltaY * zoomSpeed
    const clampedRadius = Math.max(minRadius, Math.min(maxRadius, targetRadius))

    // TODO： 优化 统一在updateCameraPosition处理
    gsap.to(this, {
      duration: 0.2,
      radius: clampedRadius,
      ease: 'power3.out',
      onUpdate: () => {
        this.updateCameraPosition()
      },
    })
  }

  private updateCameraPosition() {
    // TODO： 有些生硬，加些过度动画
    this.object.position.set(
      this.radius * Math.sin(this.rotationV) * Math.sin(this.rotationH),
      this.radius * Math.cos(this.rotationV),
      this.radius * Math.sin(this.rotationV) * Math.cos(this.rotationH),
    )

    this.update()

    this.dispatchEvent({
      type: 'change',
      position: this.object.position,
      target: this.target,
    })
  }
}

export default FollowOrbitControls
